import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useBulkDeleteVitals,
  useDeleteVitals,
  useVitals,
} from '../../services/httpServices/queries';
import type { VitalSigns } from '../../services/httpServices/vitalsService';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { joinFullName } from '../../utils/patientName';
import { VitalsFormModal } from './VitalsFormModal';
import { VitalsDetailsModal } from './VitalsDetailsModal';
import {
  Activity,
  AlertCircle,
  Eye,
  HeartPulse,
  Loader2,
  Pencil,
  Plus,
  Search,
  Square,
  SquareCheck,
  Thermometer,
  Trash2,
  Wind,
  X,
} from 'lucide-react';

/** Format a numeric vital + unit, falling back to an em dash. */
function withUnit(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${value}${unit}`;
}

export function Vitals() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isNewRoute = location.pathname.endsWith('/new');

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });

  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(isNewRoute);
  const [editingRecord, setEditingRecord] = useState<VitalSigns | null>(null);
  const [detailsRecord, setDetailsRecord] = useState<VitalSigns | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);

  const { data, isLoading, error } = useVitals(page, limit, debouncedSearch || undefined);
  const deleteVitals = useDeleteVitals();
  const bulkDeleteVitals = useBulkDeleteVitals();

  const vitals: VitalSigns[] = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);

    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // A page change invalidates the current selection.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, debouncedSearch]);

  const clearFilters = () => {
    setSearch('');
    setPage(1);
  };

  const isDeleting = deleteVitals.isPending || bulkDeleteVitals.isPending;

  const closeForm = () => {
    setFormOpen(false);
    setEditingRecord(null);
    if (isNewRoute) {
      const query = params.toString();
      navigate(`/ipd/vitals${query ? `?${query}` : ''}`, { replace: true });
    }
  };

  const openCreate = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };

  const openEdit = (record: VitalSigns) => {
    setDetailsRecord(null);
    setEditingRecord(record);
    setFormOpen(true);
  };

  const requestDelete = (record: VitalSigns) => {
    setDetailsRecord(null);
    setPendingDeleteIds([record.id]);
  };

  const allSelected = vitals.length > 0 && vitals.every((vital) => selectedIds.includes(vital.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !vitals.some((vital) => vital.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...vitals.map((vital) => vital.id)])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleConfirmDelete = async () => {
    const ids = pendingDeleteIds;
    if (ids.length === 0) return;

    try {
      if (ids.length === 1) {
        await deleteVitals.mutateAsync(ids[0]);
      } else {
        await bulkDeleteVitals.mutateAsync(ids);
      }
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      setPendingDeleteIds([]);
      if (page > 1 && ids.length >= vitals.length) {
        setPage((current) => Math.max(1, current - 1));
      }
    } catch {
      // Global mutation toast reports the failure; keep the dialog open.
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <HeartPulse className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Vitals</h1>
            <p className="text-xs text-text-muted">Patient observations recorded across active visits</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
        >
          <Plus size={16} />
          Record Vitals
        </button>
      </div>

      {/* Search */}
      <div className="rounded-xl border border-border bg-surface p-3">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search by patient name or registration number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-4 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          {search && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-border px-3 py-2 text-sm text-text-muted transition-colors hover:border-danger hover:text-danger"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-danger/20 bg-danger/10 p-3 text-sm text-danger">
          <AlertCircle size={18} />
          <span>Failed to load vitals. Please try again.</span>
        </div>
      )}

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-surface p-2.5">
          <span className="mr-1 text-sm text-text-muted">
            <span className="font-medium text-text">{selectedIds.length}</span> selected
          </span>
          <button
            onClick={() => setPendingDeleteIds(selectedIds)}
            disabled={isDeleting}
            className="flex items-center gap-1.5 rounded-lg bg-danger px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-danger/90 disabled:opacity-50"
          >
            {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete selected
          </button>
          <button
            onClick={() => setSelectedIds([])}
            disabled={isDeleting}
            className="rounded-lg px-2.5 py-1.5 text-xs text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="w-10 px-3 py-2.5 text-left">
                    <button
                      onClick={toggleSelectAll}
                      className="text-text-muted transition-colors hover:text-text"
                      aria-label="Select all vitals on this page"
                    >
                      {allSelected ? (
                        <SquareCheck size={18} className="text-primary" />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Patient
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Temp
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    BP
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Pulse
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    SpO₂
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    RR
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Weight / BMI
                  </th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Recorded
                  </th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-medium uppercase tracking-wider text-text-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {vitals.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>
                          No vitals match your search.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <HeartPulse size={22} className="text-text-muted/60" />
                          <span>No vitals recorded yet.</span>
                          <button
                            onClick={openCreate}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            Record the first set
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  vitals.map((vital) => {
                    const isSelected = selectedIds.includes(vital.id);
                    const recordedAt = new Date(vital.createdAt);
                    return (
                      <tr
                        key={vital.id}
                        className={`transition-colors hover:bg-background/50 ${
                          isSelected ? 'bg-primary/5' : ''
                        }`}
                      >
                        <td className="px-3 py-2.5">
                          <button
                            onClick={() => toggleSelectOne(vital.id)}
                            className="text-text-muted transition-colors hover:text-text"
                            aria-label={`Select vitals for ${joinFullName(vital.patient || {}) || 'patient'}`}
                          >
                            {isSelected ? (
                              <SquareCheck size={18} className="text-primary" />
                            ) : (
                              <Square size={18} />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            onClick={() => setDetailsRecord(vital)}
                            className="text-left text-sm font-medium text-text transition-colors hover:text-primary hover:underline"
                            title="View vitals details"
                          >
                            {joinFullName(vital.patient || {}) || 'Unknown patient'}
                          </button>
                          <p className="text-[11px] font-mono text-text-muted">
                            {vital.patient?.registrationNumber || '—'}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="flex items-center gap-1 text-sm text-text">
                            <Thermometer size={13} className="text-text-muted" />
                            {vital.temperature != null ? `${vital.temperature}°C` : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="flex items-center gap-1 text-sm text-text">
                            <Activity size={13} className="text-text-muted" />
                            {vital.bloodPressure || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="flex items-center gap-1 text-sm text-text">
                            <HeartPulse size={13} className="text-text-muted" />
                            {withUnit(vital.pulse, ' bpm')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="flex items-center gap-1 text-sm text-text">
                            <Wind size={13} className="text-text-muted" />
                            {withUnit(vital.spo2, '%')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="flex items-center gap-1 text-sm text-text">
                            <Wind size={13} className="text-text-muted" />
                            {withUnit(vital.respiratoryRate, '/min')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-sm text-text">
                            {withUnit(vital.weight, ' kg')}
                          </span>
                          <p className="text-[11px] text-text-muted">
                            BMI {vital.bmi != null ? vital.bmi : '—'}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-sm text-text">{recordedAt.toLocaleDateString()}</span>
                          <p className="text-[11px] text-text-muted">
                            {recordedAt.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => setDetailsRecord(vital)}
                              title="View details"
                              aria-label="View vitals details"
                              className="rounded-lg border border-border bg-surface p-1.5 text-text transition-colors hover:bg-background hover:text-primary"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              onClick={() => openEdit(vital)}
                              title="Edit"
                              aria-label="Edit vitals"
                              className="rounded-lg border border-border bg-surface p-1.5 text-text transition-colors hover:bg-background hover:text-primary"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => requestDelete(vital)}
                              title="Delete"
                              aria-label="Delete vitals"
                              className="rounded-lg bg-danger/10 p-1.5 text-danger transition-colors hover:bg-danger/20"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="records"
          />
        </div>
      )}

      {/* Record / edit modal */}
      <VitalsFormModal
        open={formOpen}
        record={editingRecord}
        onClose={closeForm}
        onSaved={() => {
          if (!editingRecord) setPage(1);
        }}
      />

      {/* Details modal */}
      <VitalsDetailsModal
        record={detailsRecord}
        onClose={() => setDetailsRecord(null)}
        onEdit={openEdit}
        onDelete={requestDelete}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDeleteIds.length > 0}
        title={`Delete ${pendingDeleteIds.length} vital record${pendingDeleteIds.length === 1 ? '' : 's'}?`}
        description={
          pendingDeleteIds.length === 1
            ? 'This vital record will be removed from the patient timeline. This cannot be undone.'
            : `The ${pendingDeleteIds.length} selected vital records will be removed from the patient timeline. This cannot be undone.`
        }
        confirmLabel={`Delete ${pendingDeleteIds.length === 1 ? 'record' : `${pendingDeleteIds.length} records`}`}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) setPendingDeleteIds([]);
        }}
      />
    </div>
  );
}
